// Guided TSL conversion pre-fill for the recognized custom legacy blocks (HTF EMA / Sweep+Failure /
// Displacement Gate). Produces a BEST-EFFORT TSL-text starting point for the Block Studio — using only
// whitelisted primitives (htf last-closed-bucket / lookahead-off, OHLCV sources, bounded historical
// indexing via prev(), comparisons, logic). It NEVER executes the legacy JavaScript. The user must run
// parse + type + gate + no-lookahead + preview validation in the Studio before accepting; these are
// starting points, not guaranteed-equivalent conversions.

export interface ConversionResult {
  target: string;
  tslText: string;       // pre-fill for the TSL Studio (must still pass the Studio gates)
  confidence: 'review';  // always review — best-effort, user must validate
  notes: string;
}

const n = (v: unknown, d: number) => { const x = Number(v); return Number.isFinite(x) ? x : d; };

/**
 * Best-effort TSL pre-fill for a recognized custom block. `params` is the legacy block's data.parameters.
 * Returns null for an unknown target (caller keeps it quarantined).
 */
export function convertCustomToTsl(target: string, params: Record<string, unknown> = {}): ConversionResult | null {
  switch (target) {
    case 'htf_ema': {
      // HTF EMA 200 → the safe htf() primitive (last-CLOSED higher-timeframe bucket, lookahead-off).
      // kindCode 1 = EMA. The downstream expression blocks (close vs this value) carry the long/short side.
      const period = n(params.period, 200), htfMin = n(params.htfMinutes, 60);
      return {
        target, confidence: 'review',
        notes: 'HTF EMA via the causal htf() primitive (last-closed bucket; never the still-forming bar). The existing entry/exit expression blocks (close vs this value) provide the long/short side and connect unchanged.',
        tslText: [
          'indicator {',
          `  param period: int 5..400 = ${period}`,
          `  param htfMinutes: int 1..1440 = ${htfMin}`,
          '  value: htf(htfMinutes, period, 1)',
          '  signal: (close > htf(htfMinutes, period, 1))',
          '}',
        ].join('\n'),
      };
    }
    case 'displacement_gate': {
      // Displacement = a large-body bar relative to ATR, above a floor. A filter emitting the gate signal.
      const ap = n(params.atrPeriod, 14), bm = n(params.bodyMult, 1.5), ma = n(params.minAtr, 0.1);
      return {
        target, confidence: 'review',
        notes: 'Displacement gate as a TSL filter: body size exceeds bodyMult × ATR and ATR is above minAtr. Causal (current-bar ATR + body). Verify the threshold matches the legacy intent in the preview.',
        tslText: [
          'filter {',
          `  param atrPeriod: int 2..200 = ${ap}`,
          `  param bodyMult: float 0.1..10 = ${bm}`,
          `  param minAtr: float 0..1000 = ${ma}`,
          '  signal: ((abs((close - open)) > (atr(atrPeriod) * bodyMult)) and (atr(atrPeriod) > minAtr))',
          '}',
        ].join('\n'),
      };
    }
    case 'sweep_failure': {
      // Sweep + Failure (best-effort, LONG bias): price sweeps the prior N-bar low then closes back up.
      // prev(lowest(...),1) keeps it causal (no current bar in the reference window). Short side mirrors
      // with highest/close<open — flagged for the user to confirm/duplicate per direction.
      const lb = n(params.lookback, 20);
      return {
        target, confidence: 'review',
        notes: 'Sweep+Failure (LONG bias): swept the prior lookback-bar low (prev() keeps it lookahead-off) and closed back bullish. For the SHORT block, mirror with highest()/close<open. This is an approximation — confirm against the legacy behaviour in the preview before accepting.',
        tslText: [
          'filter {',
          `  param lookback: int 2..200 = ${lb}`,
          '  signal: ((low < prev(lowest(low, lookback), 1)) and (close > open))',
          '}',
        ].join('\n'),
      };
    }
    default:
      return null;
  }
}
